import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import AgendaHuecos from '../ordenes/AgendaHuecos';
import DiaZona, { zonaCorta } from './DiaZona';
import SugerirDia from './SugerirDia';
import SugerenciaZona from './SugerenciaZona';
import SinConfirmarSheet from '../ordenes/SinConfirmarSheet';
import { formatDateISO } from '../../utils/dateUtils';

// Vista "Agenda" de Trabajos (27-oct-2026): juntó la agenda por franjas (que estaba en el Inicio) y la Semana
// de zonas (que era una ventana aparte). Arriba los días con su zona; al tocar uno, su cabecera (zona, cupo,
// lo reservado, ✨ Armar este día, Cambiar zona) y debajo las franjas con quién va y quién está libre.
// Arranca en el próximo día hábil: lo de hoy está en el Inicio.
const proximoHabil = () => { const d = new Date(); d.setDate(d.getDate() + 1); if (d.getDay() === 0) d.setDate(d.getDate() + 1); return formatDateISO(d); };

export default function AgendaTrabajos({ ordenes, tecnicos, onAbrirVisita, onArmar, onCambio, fechaInicial = null }) {
    const [dia, setDia] = useState(() => fechaInicial || proximoHabil());
    const [datos, setDatos] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [verSinDia, setVerSinDia] = useState(false);
    const [verSinConfirmar, setVerSinConfirmar] = useState(false);
    const cargar = useCallback(() => api.get('/mapa/semana').then(r => setDatos(r.data)).catch(() => toast.error('No se pudo cargar la agenda')), []);
    useEffect(() => { cargar(); }, [cargar]);

    const porFecha = useMemo(() => Object.fromEntries((datos?.dias || []).map(d => [String(d.fecha).slice(0, 10), d])), [datos]);
    const d = porFecha[dia];
    const cambiar = async (cambios) => {
        setGuardando(true);
        try { await api.put(`/mapa/semana/${dia}`, cambios); await cargar(); onCambio?.(); }
        catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); }
        finally { setGuardando(false); }
    };
    const sinDia = datos?.sinDia || [];
    // Zonas sugeridas (27-oct-2026): la del día elegido va arriba de su cabecera; las de otros días, en una línea
    const sugerencias = datos?.sugerenciasZona || [];
    const sugDe = Object.fromEntries(sugerencias.map(x => [String(x.fecha).slice(0, 10), x]));
    const otras = sugerencias.filter(x => String(x.fecha).slice(0, 10) !== dia);
    const listo = () => { cargar(); onCambio?.(); };

    // 4-nov-2026: lo de ese día que sigue con fecha tentativa, a un toque
    const tentativas = (ordenes || []).filter(o => o.sinConfirmar && o.estado === 'PENDIENTE' && String(o.fechaProgramada).slice(0, 10) === dia).length;
    const cabecera = d ? (
        <DiaZona d={d} dias={datos.dias} guardando={guardando} onCambiar={cambiar} onListo={cargar} onArmar={() => onArmar(dia)}
            extra={sugDe[dia] ? <SugerenciaZona s={sugDe[dia]} onListo={listo} /> : null} />
    ) : null;

    return (
        <div className="space-y-3">
            {otras.map(x => (
                <button key={x.fecha} type="button" onClick={() => setDia(String(x.fecha).slice(0, 10))}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-[#3B5BDB]/50 bg-[rgba(59,91,219,0.10)] text-left active:scale-[0.99]">
                    <span className="text-body font-black text-ink">✨ Zona sugerida: <span className="capitalize">{x.dia}</span> a {zonaCorta(x.hacia)}</span>
                    <span className="text-caption font-bold text-muted shrink-0">Ver ›</span>
                </button>
            ))}
            {sinDia.length > 0 && (
                <div className="rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06]">
                    <button type="button" onClick={() => setVerSinDia(v => !v)} aria-expanded={verSinDia}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
                        <span className="text-body font-black text-ink">{sinDia.length} pedido{sinDia.length !== 1 ? 's' : ''} sin día reservado</span>
                        <span className="text-caption font-bold text-muted">{verSinDia ? 'Ocultar' : 'Sugerirles un día ›'}</span>
                    </button>
                    {verSinDia && (
                        <div className="px-4 pb-2">
                            {sinDia.map(p => (
                                <div key={p.pedidoId} className="py-2 border-t border-black/[0.06] dark:border-white/[0.06]">
                                    <span className="flex items-center gap-1.5 min-w-0">
                                        {p.urgente && <span className="h-5 px-1.5 shrink-0 rounded-md bg-brand-red text-white text-label font-black uppercase inline-flex items-center">Urgente</span>}
                                        <span className="text-body font-black text-ink truncate">{p.nombre}</span>
                                        {p.zonaNombre && <span className="text-caption text-muted shrink-0">· {p.zonaNombre}</span>}
                                    </span>
                                    <span className="block text-caption text-muted truncate">{[p.motivo, p.direccion].filter(Boolean).join(' · ')}</span>
                                    <SugerirDia p={p} dias={datos.dias} onListo={cargar} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {tentativas > 0 && (
                <button type="button" onClick={() => setVerSinConfirmar(true)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-[#D97706]/50 bg-[rgba(217,119,6,0.10)] text-left active:scale-[0.99]">
                    <span className="text-body font-black text-ink">⏳ {tentativas} visita{tentativas !== 1 ? 's' : ''} sin confirmar este día</span>
                    <span className="text-caption font-bold text-muted shrink-0">Resolver ›</span>
                </button>
            )}
            {verSinConfirmar && <SinConfirmarSheet fecha={dia} onCerrar={() => setVerSinConfirmar(false)} onCambio={onCambio} />}
            <div className="rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] p-3">
                <AgendaHuecos modo="ver" tecnicos={tecnicos} ordenes={ordenes} fecha={dia} onFecha={setDia}
                    onAbrirVisita={onAbrirVisita} cabecera={cabecera} onCambio={onCambio}
                    etiquetaDia={(iso) => (sugDe[iso] ? `¿${zonaCorta(sugDe[iso].hacia)}?` : porFecha[iso] ? zonaCorta(porFecha[iso].zona) : '')} />
            </div>
        </div>
    );
}
